import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Checkbox,
  FormControlLabel,
  Link,
} from '@mui/material';
import {
  Close as CloseIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from 'axios';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const RegistrationModal = ({ open, onClose, onSuccess }) => {
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);
  const [isLogin, setIsLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [acceptedDisclaimer, setAcceptedDisclaimer] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (phone.length === 10) {
      // Auto-fill PIN with last 4 digits
      const lastFour = phone.slice(-4);
      setPin(lastFour);
      setConfirmPin(lastFour);
    }
  }, [phone]);

  const validatePhone = (phoneNumber) => {
    const cleaned = phoneNumber.replace(/\D/g, '');
    return cleaned.length === 10;
  };

  const validatePin = (pinValue) => {
    return /^\d{4}$/.test(pinValue);
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setPhone(value);
    setErrors({ ...errors, phone: '' });
  };

  const handlePinChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setPin(value);
    setErrors({ ...errors, pin: '' });
  };

  const handleConfirmPinChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setConfirmPin(value);
    setErrors({ ...errors, confirmPin: '' });
  };

  const handleSubmit = async () => {
    const newErrors = {};

    if (!validatePhone(phone)) {
      newErrors.phone = 'Please enter a valid 10-digit mobile number';
    }

    if (!validatePin(pin)) {
      newErrors.pin = 'PIN must be 4 digits';
    }

    if (!isLogin && pin !== confirmPin) {
      newErrors.confirmPin = 'PINs do not match';
    }

    if (!isLogin && !acceptedDisclaimer) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const normalizedPhone = `+91${phone}`;
      
      if (isLogin) {
        // Login
        const response = await axios.post(`${API_BASE_URL}/auth/login`, {
          phone: normalizedPhone,
          password: pin,
        });

        if (response.data.success) {
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          toast.success('Login successful!');
          onSuccess(response.data.user);
          handleClose();
        }
      } else {
        // Registration
        const response = await axios.post(`${API_BASE_URL}/auth/register`, {
          phone: normalizedPhone,
          password: pin,
          full_name: `User ${phone}`,
        });

        if (response.data.success) {
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          // Show credentials to the user (ID + PIN)
          const createdUserId = response.data.user?.id || 'N/A';
          toast.success(`Registration successful!\nUser ID: ${createdUserId}\nPassword (PIN): ${pin}`);
          
          // Auto-navigate to User Login with phone & PIN (auto-login supported)
          try {
            const cleanPhone = phone.replace(/\D/g, '').slice(0, 10);
            const targetUrl = `http://localhost:3001/user-login?phone=${cleanPhone}&password=${pin}`;
            // Small delay to let the toast render, then redirect
            setTimeout(() => {
              window.location.href = targetUrl;
            }, 800);
          } catch (navErr) {
            console.warn('Auto login navigation failed:', navErr);
          }
          onSuccess(response.data.user);
          handleClose();
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      const errorMessage = error.response?.data?.message || 
        (isLogin ? 'Login failed. Please check your credentials.' : 'Registration failed. Phone number may already be registered.');
      toast.error(errorMessage);
      
      if (error.response?.status === 401 && isLogin) {
        // If login fails, suggest registration
        setIsLogin(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPhone('');
    setPin('');
    setConfirmPin('');
    setIsLogin(false);
    setErrors({});
    setAcceptedDisclaimer(false);
    onClose();
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setErrors({});
    setPin('');
    setConfirmPin('');
    if (!isLogin) {
      // When switching to login, auto-fill PIN if phone exists
      if (phone.length === 10) {
        setPin(phone.slice(-4));
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        },
      }}
    >
      <DialogTitle sx={{ color: 'white', pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={700}>
            {isLogin ? '🔐 Login' : '📱 Register'}
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: 'white', mt: 1 }}>
        <Box sx={{ py: 2 }}>
          {/* Phone Number Field */}
          <TextField
            fullWidth
            label="Mobile Number"
            value={phone}
            onChange={handlePhoneChange}
            error={!!errors.phone}
            helperText={errors.phone || 'Enter your 10-digit mobile number'}
            InputProps={{
              startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ mb: 2 }}
            placeholder="9876543210"
          />

          {/* PIN Field */}
          <TextField
            fullWidth
            label="4-Digit PIN"
            type={showPin ? 'text' : 'password'}
            value={pin}
            onChange={handlePinChange}
            error={!!errors.pin}
            helperText={errors.pin || (phone.length === 10 ? 'Default: Last 4 digits of your phone number' : 'Enter 4-digit PIN')}
            InputProps={{
              startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              endAdornment: (
                <IconButton onClick={() => setShowPin(!showPin)} edge="end">
                  {showPin ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
            sx={{ mb: isLogin ? 2 : 1 }}
            placeholder="1234"
          />

          {/* Confirm PIN (only for registration) */}
          {!isLogin && (
            <TextField
              fullWidth
              label="Confirm PIN"
              type={showConfirmPin ? 'text' : 'password'}
              value={confirmPin}
              onChange={handleConfirmPinChange}
              error={!!errors.confirmPin}
              helperText={errors.confirmPin || 'Re-enter your PIN to confirm'}
              InputProps={{
                startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                endAdornment: (
                  <IconButton onClick={() => setShowConfirmPin(!showConfirmPin)} edge="end">
                    {showConfirmPin ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
              sx={{ mb: 2 }}
              placeholder="1234"
            />
          )}

          {/* PIN Info */}
          {phone.length === 10 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                💡 <strong>Default PIN:</strong> Last 4 digits ({phone.slice(-4)}) will be auto-filled.
                You can change it to your preferred PIN.
              </Typography>
            </Alert>
          )}

          {/* Disclaimer (only for registration) */}
          {!isLogin && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="warning" sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  📋 Terms & Conditions
                </Typography>
                <Typography variant="caption" component="div">
                  <strong>Job Alerts:</strong> By registering, you agree to receive job alerts and notifications. 
                  We are not responsible for the accuracy of job postings or hiring decisions by employers.
                </Typography>
                <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                  <strong>Coupons:</strong> Coupons are subject to terms and conditions of respective services. 
                  LOCSY is not liable for coupon redemption issues or service provider disputes.
                </Typography>
              </Alert>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptedDisclaimer}
                    onChange={(e) => setAcceptedDisclaimer(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    I accept the <Link href="#" onClick={(e) => { e.preventDefault(); }}>Terms & Conditions</Link>
                  </Typography>
                }
              />
            </Box>
          )}

          {/* Mode Toggle */}
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button onClick={toggleMode} size="small" variant="text">
              {isLogin 
                ? "Don't have an account? Register" 
                : 'Already registered? Login'}
            </Button>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ bgcolor: 'white', px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !phone || !pin || (!isLogin && (!acceptedDisclaimer || pin !== confirmPin))}
          sx={{
            bgcolor: '#667eea',
            '&:hover': { bgcolor: '#5568d3' },
            minWidth: 120,
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            isLogin ? 'Login' : 'Register'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegistrationModal;

